import { ApolloLink } from '@apollo/client';
import type { CustomWaterfallRange, PerformanceDatapointType } from 'owa-analytics-types';
import type { ResolverContext } from 'owa-graph-schema';
import { getOperationType } from '../util/getOperationType';
import { lazyLogQueryDuplication } from 'owa-logging-utils/lib/lazyFunctions';

export function analyticsLink(): ApolloLink {
    const link = new ApolloLink((operation, forward) => {
        const context = operation.getContext() as ResolverContext;
        const perfDatapoint = context?.perfDatapoint;
        const waterfallSettings = context?.waterfallSettings;
        if (perfDatapoint) {
            perfDatapoint.didExecuteGqlQuery = true;
        }

        // Detect and report pathological query duplication
        lazyLogQueryDuplication.importAndExecute(operation);

        if (getOperationType(operation) === 'subscription') {
            return forward(operation);
        } else {
            // A dev needs to explicitely ask to opt-out of the GQL Checkpoints by setting disableGQLTimings to true.
            // See https://aka.ms/OWALayerCakeGraph for more information.
            if (perfDatapoint && !waterfallSettings?.disableGQLTimings) {
                perfDatapoint.addToPredefinedOrCustomWaterfall(
                    'GQL_Link_S',
                    waterfallSettings?.customGQLIndexes?.[0],
                    waterfallSettings?.discardIfDefined
                );
            }

            operation.setContext({
                ...context,
                perfDatapoint,
            });

            return forward(operation).map(result => {
                if (perfDatapoint && !waterfallSettings?.disableGQLTimings) {
                    perfDatapoint.addToPredefinedOrCustomWaterfall(
                        'GQL_Link_E',
                        waterfallSettings?.customGQLIndexes?.[1],
                        waterfallSettings?.discardIfDefined
                    );

                    if (result?.extensions) {
                        // X-BackendServiceLatencies is keyed by microservice name; the 1st entry
                        // holds the latencies for the microservice that served the request.
                        const backendServiceLatencies = Object.values(
                            result.extensions['X-BackendServiceLatencies'] || {}
                        )[0];

                        // X-Total-Latency: gateway's perceived latency of the microservice call.
                        addBackendLatencyToWaterfall(
                            perfDatapoint,
                            1,
                            'WF_GW_TotalLatency',
                            backendServiceLatencies?.['X-Total-Latency']
                        );

                        // X-BackendLatency: microservice self-measured latency.
                        addBackendLatencyToWaterfall(
                            perfDatapoint,
                            2,
                            'WF_GW_BackendLatency',
                            backendServiceLatencies?.['X-BackendLatency']
                        );
                    }

                    operation.setContext({
                        ...(operation.getContext() as ResolverContext),
                        perfDatapoint,
                    });
                }

                // Add the data analytics data to the extensions field
                const resultContext = operation.getContext() as ResolverContext;

                if (resultContext?.perfDatapoint) {
                    if (!result.extensions) {
                        result.extensions = {};
                    }

                    if (!result.extensions.analytics) {
                        result.extensions.analytics = {};
                    }

                    result.extensions.analytics = {
                        perfDatapoint: resultContext?.perfDatapoint?.toJSObject?.(),
                    };
                }

                return result;
            });
        }
    });

    return link;
}

function addBackendLatencyToWaterfall(
    perfDatapoint: PerformanceDatapointType,
    index: CustomWaterfallRange,
    checkpoint: string,
    latency: unknown
) {
    // Skip missing values so the column never falls back to elapsed time. The value is passed as a
    // string because addToCustomWaterfall treats a numeric 0 as "no custom value".
    if (typeof latency === 'number' && Number.isFinite(latency) && latency >= 0) {
        perfDatapoint.addToCustomWaterfall(
            index,
            checkpoint,
            true /* discardIfDefined */,
            Math.round(latency).toString()
        );
    }
}
